import { Check, Copy, KeyRound, Loader2, Mail } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import {
  AdminUser,
  CreatedUser,
  CustomerRow,
  ENGLISH_ONLY,
  NewUserOptions,
  WelcomeResult,
  apiAdminCreateUser,
  apiAdminNewUserOptions,
  apiAdminSendWelcome,
  featureHelp
} from './api';

// Create user — replaces the hand SQL in docs/new-signals-user.md §2.
//
// Three things the form has to carry, because each was a real mistake with
// the SQL:
//   * the password is generated by the server and shown here ONCE. It is not
//     in the welcome email, and nothing can show it again.
//   * topics are chosen by their topic-guide label. "Every topic" means the
//     user follows the company, topics added later included; "only these" is
//     a fixed list.
//   * the welcome email goes to a mailbox someone names: a sign-in email is
//     not always a real mailbox. A failed send never undoes the user.

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

export function WelcomeOutcome({ result }: { result: WelcomeResult }) {
  const tone =
    result.status === 'sent'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
      : result.status === 'refused'
        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
        : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200';
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${tone}`}>
      <div className="flex items-start gap-2">
        <Mail className="size-4 mt-0.5 shrink-0" />
        <div>
          <div>{result.message}</div>
          {result.notes.map((n) => (
            <div key={n} className="text-xs opacity-80 mt-1">
              {n}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CreateUserDialog({
  open,
  onOpenChange,
  customers,
  initialCustomerPk,
  onCreated
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers: CustomerRow[];
  initialCustomerPk: number | null;
  onCreated: (customerPk: number) => void;
}) {
  const [customerPk, setCustomerPk] = useState<number | null>(null);
  const [options, setOptions] = useState<NewUserOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [limit, setLimit] = useState('');
  const [lastDay, setLastDay] = useState('');
  const [access, setAccess] = useState<'all' | 'some'>('all');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [englishOnly, setEnglishOnly] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [sendTo, setSendTo] = useState('');
  const [sendToTouched, setSendToTouched] = useState(false);
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatedUser | null>(null);
  const [copied, setCopied] = useState(false);

  // A fresh form each time the dialog opens: a password from the last
  // creation must never be on screen for the next one.
  useEffect(() => {
    if (!open) return;
    setCustomerPk(initialCustomerPk ?? customers[0]?.pk ?? null);
    setEmail('');
    setLimit('');
    setLastDay('');
    setAccess('all');
    setChosen(new Set());
    setEnglishOnly(false);
    setSendWelcome(true);
    setSendTo('');
    setSendToTouched(false);
    setCompany('');
    setError(null);
    setResult(null);
    setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open || customerPk === null) return;
    let cancelled = false;
    setOptions(null);
    setLoadError(null);
    setAccess('all');
    setChosen(new Set());
    apiAdminNewUserOptions(customerPk)
      .then((o) => {
        if (cancelled) return;
        setOptions(o);
        setLimit(String(o.default_message_limit));
        setCompany((c) => c || o.last_company || '');
      })
      .catch((e) => !cancelled && setLoadError(e.message));
    return () => {
      cancelled = true;
    };
  }, [open, customerPk]);

  useEffect(() => {
    if (!sendToTouched) setSendTo(email.trim());
  }, [email, sendToTouched]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [open]);

  const problem = useMemo(() => {
    if (!options) return 'Loading the company…';
    if (!EMAIL.test(email.trim())) return 'Type the sign-in email.';
    if (limit.trim() === '' || !/^\d+$/.test(limit.trim()))
      return 'The monthly message limit is a whole number.';
    if (access === 'some' && chosen.size === 0)
      return 'Pick at least one topic, or give access to every topic.';
    if (sendWelcome && !EMAIL.test(sendTo.trim()))
      return "'Send welcome email to' is not an email address.";
    if (sendWelcome && !company.trim())
      return 'Type the company name the email greets.';
    return null;
  }, [options, email, limit, access, chosen, sendWelcome, sendTo, company]);

  const submit = async () => {
    if (problem || customerPk === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiAdminCreateUser({
        customer_pk: customerPk,
        email: email.trim(),
        base_message_limit: Number(limit.trim()),
        last_day: lastDay || undefined,
        topics: access === 'all' ? null : Array.from(chosen),
        welcome: sendWelcome
          ? { send_to: sendTo.trim(), company: company.trim() }
          : null,
        english_only: englishOnly
      });
      setResult(created);
      onCreated(customerPk);
    } catch (e: any) {
      // The server says why (login taken, above the plan, ...): verbatim.
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const seats =
    options && options.max_users !== null
      ? `${options.user_count} of ${options.max_users} users`
      : options
        ? `${options.user_count} users`
        : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        // A stray click outside must not throw away a password that is
        // shown only once; Done or Esc closes it on purpose.
        onInteractOutside={(e) => result && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{result ? 'User created' : 'Create user'}</DialogTitle>
          <DialogDescription>
            {result
              ? 'Hand the password over yourself. It is shown only here.'
              : 'A chat user on one company. Every change is in the audit log.'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
              {result.user.email} can sign in now.
            </div>
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <KeyRound className="size-4" /> Password
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-lg tracking-wider select-all">
                  {result.password}
                </code>
                <Button variant="outline" size="sm" onClick={copy}>
                  {copied ? (
                    <Check className="size-4 mr-1" />
                  ) : (
                    <Copy className="size-4 mr-1" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <Hint>
                Shown once. It is not in the welcome email and cannot be shown
                again; give it by phone or in person.
              </Hint>
            </div>
            {result.welcome ? (
              <>
                <WelcomeOutcome result={result.welcome} />
                {result.welcome.status !== 'sent' && (
                  <Hint>
                    The user is created. Use "Send welcome email" on their row
                    to try again.
                  </Hint>
                )}
              </>
            ) : (
              <Hint>
                No welcome email was sent. "Send welcome email" on the user's
                row sends it later.
              </Hint>
            )}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="grid gap-1.5">
              <Label>Company</Label>
              <Select
                value={customerPk === null ? undefined : String(customerPk)}
                onValueChange={(v) => setCustomerPk(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a company" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.pk} value={String(c.pk)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Hint>
                {options?.customer.database
                  ? `Database ${options.customer.database} · ${seats}`
                  : seats}
              </Hint>
              {loadError && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {loadError}
                </p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="cu-email">Sign-in email</Label>
              <Input
                id="cu-email"
                type="email"
                autoComplete="off"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Hint>
                Role: user (chat). Admin accounts are made by BuduCloud.
              </Hint>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="cu-limit">Messages per month</Label>
                <Input
                  id="cu-limit"
                  inputMode="numeric"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                />
                <Hint>
                  {options?.max_message_limit != null
                    ? `The plan allows up to ${options.max_message_limit}.`
                    : 'No plan ceiling set.'}
                </Hint>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="cu-last">Last day of access</Label>
                <Input
                  id="cu-last"
                  type="date"
                  min={today}
                  value={lastDay}
                  onChange={(e) => setLastDay(e.target.value)}
                />
                <Hint>
                  Empty = no end date. They can still work on this day.
                </Hint>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Topics</Label>
              <RadioGroup
                value={access}
                onValueChange={(v) => setAccess(v as 'all' | 'some')}
                className="gap-2"
              >
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="all" className="mt-0.5" />
                  <span>
                    Every topic
                    <span className="block text-xs text-muted-foreground">
                      Follows the company, topics added later included.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-2 text-sm ${
                    options?.can_choose_topics && options.topics.length
                      ? 'cursor-pointer'
                      : 'opacity-50'
                  }`}
                >
                  <RadioGroupItem
                    value="some"
                    className="mt-0.5"
                    disabled={
                      !options?.can_choose_topics || !options.topics.length
                    }
                  />
                  <span>
                    Only these topics
                    <span className="block text-xs text-muted-foreground">
                      {!options
                        ? ''
                        : !options.can_choose_topics
                          ? "This company's database cannot limit topics yet."
                          : !options.topics.length
                            ? 'This company has no labelled topics yet.'
                            : 'A fixed list; a new topic is not added by itself.'}
                    </span>
                  </span>
                </label>
              </RadioGroup>
              {access === 'some' && options && (
                <div className="rounded-lg border p-3 grid gap-2 sm:grid-cols-2">
                  {options.topics.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-start gap-2 text-sm cursor-pointer"
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={chosen.has(t.id)}
                        onCheckedChange={(v) =>
                          setChosen((prev) => {
                            const next = new Set(prev);
                            if (v) next.add(t.id);
                            else next.delete(t.id);
                            return next;
                          })
                        }
                      />
                      <span>
                        {t.label}
                        {t.summary && (
                          <span className="block text-xs text-muted-foreground">
                            {t.summary}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                  {options.supporting_count > 0 && (
                    <p className="sm:col-span-2 text-xs text-muted-foreground border-t pt-2">
                      Plus {options.supporting_count} supporting table
                      {options.supporting_count === 1 ? '' : 's'} (item and
                      party lists) that every topic joins to.
                    </p>
                  )}
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                className="mt-0.5"
                checked={englishOnly}
                onCheckedChange={(v) => setEnglishOnly(!!v)}
              />
              <span>
                English only
                <span className="block text-xs text-muted-foreground">
                  {featureHelp(ENGLISH_ONLY)}
                </span>
              </span>
            </label>

            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm flex gap-2">
              <KeyRound className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span>
                A password is generated and shown to you once, after the user is
                created. It is never emailed.
              </span>
            </div>

            <div className="grid gap-3">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Checkbox
                  checked={sendWelcome}
                  onCheckedChange={(v) => setSendWelcome(!!v)}
                />
                Send the welcome email
              </label>
              {sendWelcome && (
                <div className="grid gap-4 sm:grid-cols-2 pl-6">
                  <div className="grid gap-1.5">
                    <Label htmlFor="cu-sendto">Send welcome email to</Label>
                    <Input
                      id="cu-sendto"
                      type="email"
                      value={sendTo}
                      onChange={(e) => {
                        setSendToTouched(true);
                        setSendTo(e.target.value);
                      }}
                    />
                    <Hint>
                      A sign-in email is not always a real mailbox. Check it.
                    </Hint>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="cu-company">
                      Company name in the email
                    </Label>
                    <Input
                      id="cu-company"
                      placeholder="Sobhy Girgis Trading"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                    <Hint>As the customer writes it, not the system name.</Hint>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                {error}
              </div>
            )}

            <DialogFooter className="items-center gap-2">
              {problem && options && (
                <span className="text-xs text-muted-foreground mr-auto">
                  {problem}
                </span>
              )}
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={!!problem || submitting}>
                {submitting && <Loader2 className="size-4 mr-1 animate-spin" />}
                Create user
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function WelcomeEmailDialog({
  user,
  defaultCompany,
  onClose,
  onSent
}: {
  user: AdminUser | null;
  // The name this company's last welcome email used, as the create form
  // starts from it too.
  defaultCompany: string | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const [sendTo, setSendTo] = useState('');
  const [company, setCompany] = useState('');
  const [resend, setResend] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WelcomeResult | null>(null);

  useEffect(() => {
    if (!user) return;
    setSendTo(user.email);
    setCompany(defaultCompany ?? '');
    setResend(false);
    setError(null);
    setResult(null);
  }, [user]);

  const last = user?.welcome;
  const delivered = last && last.status !== 'FAILED';
  const ready =
    EMAIL.test(sendTo.trim()) && !!company.trim() && (!delivered || resend);

  const send = async () => {
    if (!user || !ready) return;
    setSending(true);
    setError(null);
    try {
      const r = await apiAdminSendWelcome(user.pk, {
        send_to: sendTo.trim(),
        company: company.trim(),
        resend
      });
      setResult(r);
      onSent();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send welcome email</DialogTitle>
          <DialogDescription>
            For {user?.email}. It never contains the password.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="flex flex-col gap-4">
            <WelcomeOutcome result={result} />
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="we-sendto">Send welcome email to</Label>
              <Input
                id="we-sendto"
                type="email"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
              />
              <Hint>
                A sign-in email is not always a real mailbox. Check it.
              </Hint>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="we-company">Company name in the email</Label>
              <Input
                id="we-company"
                placeholder="Sobhy Girgis Trading"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            {delivered && last && (
              <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                <Checkbox
                  className="mt-0.5"
                  checked={resend}
                  onCheckedChange={(v) => setResend(!!v)}
                />
                <span>
                  {last.status === 'SENT' ? 'Already sent' : 'A send started'}{' '}
                  {last.at.slice(0, 10)} to {last.send_to}. Send again only if
                  that email was lost.
                </span>
              </label>
            )}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={send} disabled={!ready || sending}>
                {sending && <Loader2 className="size-4 mr-1 animate-spin" />}
                Send
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
